import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { getForatableModalUrl } from '../lib/foratable';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const foratableModalUrl = getForatableModalUrl(language);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.menu'), href: '#menu' },
    { name: t('nav.gallery'), href: '#gallery' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-stone-950/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('#home')}
            className="flex items-center gap-2 group"
          >
            <span className="text-2xl md:text-3xl font-serif font-bold text-amber-100 group-hover:text-amber-200 transition-colors">
              Da Daniele
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-amber-100/80 hover:text-amber-200 transition-colors text-sm uppercase tracking-wider font-medium"
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
            <a href="tel:+41326236888" className="flex items-center gap-2 text-amber-200 hover:text-amber-100 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm">+41 32 623 68 88</span>
            </a>
            <Button
              data-lunchgate-modal-url={foratableModalUrl}
              data-testid="navbar-reserve-button"
              className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
            >
              {t('nav.reserve')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-amber-100 p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-stone-900/95 backdrop-blur-md rounded-2xl p-4 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left text-amber-100/80 hover:text-amber-200 hover:bg-stone-800/50 transition-colors py-3 px-4 rounded-lg text-sm uppercase tracking-wider"
              >
                {link.name}
              </button>
            ))}
            <div className="pt-2 border-t border-stone-700">
              <div className="px-4 py-2">
                <LanguageSelector />
              </div>
              <a href="tel:+41326236888" className="flex items-center gap-2 text-amber-200 py-3 px-4">
                <Phone className="w-4 h-4" />
                <span>+41 32 623 68 88</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
